import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { BarChart2, ChevronDown } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { KeywordRanking } from '../../types'
import { APP_NAME, BRAND, CONSULTATION_URL } from '../../config'
import { KeywordRankingDetail } from './KeywordRankingDetail'
import LogoIcon from '../../assets/landing/logo-icon.svg'
import {
  HeaderMetaChip,
  ReportSectionCard,
  ReportSectionHeader,
  ReportSectionBody,
} from './ReportSectionCard'
import googleLogo from '../../assets/google_symbol.svg'

function ordinalT(n: number, t: (key: string, opts?: Record<string, unknown>) => string): string {
  if (n === 1) return t('rankings.ordinal.1')
  if (n === 2) return t('rankings.ordinal.2')
  if (n === 3) return t('rankings.ordinal.3')
  return t('rankings.ordinalN', { n })
}

// foundInTopPage distinguishes "ranked but low visibility" (found on a
// deeper results page, undefined/false while isRanked=true) from a true
// top-3/top-page rank. See interfaces.KeywordRanking.FoundInTopPage.
function rowAccent(position: number, isRanked: boolean, foundInTopPage?: boolean): string {
  if (!isRanked) return '#ef042d'
  if (foundInTopPage === false) return '#f54900'
  if (position === 1) return '#00a63e'
  if (position === 2) return '#1447e6'
  if (position === 3) return '#d08700'
  return '#99a1af'
}

function positionBadgeClass(position: number, isRanked: boolean, foundInTopPage?: boolean): string {
  if (!isRanked) return 'bg-[#f3f4f6] text-[#6a7282] ring-1 ring-[#e8e8ec]'
  if (foundInTopPage === false) return 'bg-[#ffedd5] text-[#c2410c]'
  if (position === 1) return 'bg-[#dcfce7] text-[#008236]'
  if (position === 2) return 'bg-[#dbeafe] text-[#1447e6]'
  if (position === 3) return 'bg-[#fef3c6] text-[#a65f00]'
  return 'bg-[#f3f4f6] text-[#6a7282]'
}

interface RankingRowProps {
  ranking: KeywordRanking
  expanded: boolean
  onToggle: () => void
  index: number
}

function RankingRow({ ranking, expanded, onToggle, index }: RankingRowProps) {
  const { t } = useTranslation('report')
  const accent = rowAccent(ranking.position, ranking.is_ranked, ranking.found_in_top_page)

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 + index * 0.04 }}
      className={`overflow-hidden rounded-[14px] bg-[#f2f2f6] transition-shadow ${
        expanded ? 'ring-2 ring-[#01114f]/10 shadow-sm' : ''
      }`}
    >
      <div className="relative">
        <span
          className="absolute inset-y-2.5 inset-s-0 w-[3px] rounded-full"
          style={{ backgroundColor: accent }}
          aria-hidden
        />

        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="flex w-full items-center gap-3 py-3.5 ps-5 pe-4 text-start transition-colors hover:bg-white/50"
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white shadow-sm ring-1 ring-[#e8e8ec]">
            <img src={googleLogo} alt="" className="h-4 w-4" aria-hidden />
          </div>

          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium leading-snug text-[#1e2939]">
              {ranking.keyword}
            </p>
            {!ranking.is_ranked && ranking.top_result && (
              <p className="mt-0.5 truncate text-xs text-[#99a1af]">
                {t('rankings.topResult', { name: ranking.top_result })}
              </p>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <span
              className={`inline-flex h-6 items-center rounded-md px-2 text-[11px] font-bold tabular-nums ${positionBadgeClass(ranking.position, ranking.is_ranked, ranking.found_in_top_page)}`}
            >
              {ranking.is_ranked ? ordinalT(ranking.position, t) : t('rankings.unranked')}
            </span>
            <span className="hidden text-[10px] font-medium uppercase tracking-wide text-[#99a1af] sm:inline">
              {t('rankings.mapResult')}
            </span>
            <ChevronDown
              className={`h-4 w-4 shrink-0 text-[#99a1af] transition-transform duration-300 ${
                expanded ? 'rotate-180' : ''
              }`}
            />
          </div>
        </button>
      </div>
    </motion.div>
  )
}

function RankingsCallout({ unrankedCount }: { unrankedCount: number }) {
  const { t } = useTranslation('report')

  return (
    <div
      className="flex items-start gap-3 rounded-[14px] border px-4 py-4"
      style={{ borderColor: `${BRAND.red}30`, backgroundColor: `${BRAND.red}08` }}
    >
      <img src={LogoIcon} alt="logo" className="h-7 w-7 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="mb-1 text-xs font-bold text-[#01114f]">
          {t('sectionCallout.title', { appName: APP_NAME })}
        </p>
        <p className="mb-2 text-xs leading-relaxed text-[#4a5565]">
          {t('callout.rankings.message', { unranked: unrankedCount, appName: APP_NAME })}
        </p>
        <a
          href={CONSULTATION_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-bold text-[#ef042d] transition-opacity hover:opacity-90"
        >
          {t('callout.rankings.cta')}
        </a>
      </div>
    </div>
  )
}

interface SearchRankingsSectionProps {
  rankings: KeywordRanking[]
  scanId?: string
}

export function SearchRankingsSection({ rankings, scanId }: SearchRankingsSectionProps) {
  const { t, i18n } = useTranslation('report')
  const [expandedKeyword, setExpandedKeyword] = useState<string | null>(null)
  const rankedCount = rankings.filter(r => r.is_ranked).length
  const unrankedCount = rankings.length - rankedCount
  const hasIssues = unrankedCount > 0

  return (
    <ReportSectionCard delay={0.25}>
      <ReportSectionHeader
        icon={BarChart2}
        accentColor="#16a34a"
        title={t('rankings.title')}
        subtitle={t('rankings.subtitle')}
        right={
          <HeaderMetaChip>
            {t('rankings.rankedIn', { ranked: rankedCount, total: rankings.length })}
          </HeaderMetaChip>
        }
      />

      <ReportSectionBody className="space-y-2">
        {rankings.map((r, i) => {
          const expanded = expandedKeyword === r.keyword
          return (
            <div key={r.keyword || String(i)}>
              <RankingRow
                ranking={r}
                expanded={expanded}
                index={i}
                onToggle={() => setExpandedKeyword(expanded ? null : r.keyword)}
              />

              <AnimatePresence>
                {expanded && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    className="overflow-hidden"
                  >
                    <div className="pt-2">
                      <KeywordRankingDetail
                        ranking={r}
                        scanId={scanId || ''}
                        lang={i18n.language}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )
        })}

        {hasIssues && <RankingsCallout unrankedCount={unrankedCount} />}
      </ReportSectionBody>
    </ReportSectionCard>
  )
}
